/**
 * AudioManager — wraps Phaser's sound system with crossfade, layering,
 * and safe fallbacks for missing assets.
 *
 * Music: ambient loops, with crossfade between tracks.
 * SFX:   procedural via Web Audio (see SFX.js).
 * Voice: the grandmother's narration, played over memory scenes.
 */
import { SFX } from './SFX.js';

// All known music track keys — used to identify which sounds are "music"
// so we can stop them during crossfades (vs voice lines or SFX).
const MUSIC_KEYS = new Set([
  'ambient_garden', 'memory_unfold',
  'wedding_dance_memory', 'fig_tree_memory', 'blueberry_memory', 'buried_bulb_memory',
]);

class AudioManagerClass {
  constructor() {
    this.scene = null;
    this._currentMusic = null;
    this._musicVolume = 0.45;
    this._sfxVolume = 0.6;
    this._voiceVolume = 0.9;
    this._enabled = true;
  }

  /** Called by each scene during preload/create */
  attach(scene) {
    this.scene = scene;
    // Initialize procedural SFX with the same audio context Phaser uses
    if (scene.sound?.context) SFX.init(scene.sound.context);
  }

  /** Preload all audio assets — call from BootScene */
  preloadAll(scene) {
    const base = 'assets/audio';
    const music = [
      'ambient_garden', 'memory_unfold',
      'wedding_dance_memory', 'fig_tree_memory', 'blueberry_memory', 'buried_bulb_memory',
    ];
    music.forEach(slug => {
      scene.load.audio(slug, [`${base}/${slug}.mp3`, `${base}/${slug}.wav`]);
    });
    const voices = [
      'grandma_intro_1', 'grandma_intro_2', 'grandma_intro_3', 'grandma_intro_4', 'grandma_intro_5', 'grandma_intro_6',
      'grandma_fig_1', 'grandma_fig_2', 'grandma_fig_3', 'grandma_fig_4', 'grandma_fig_5', 'grandma_fig_6',
      'grandma_blue_1', 'grandma_blue_2', 'grandma_blue_3', 'grandma_blue_4', 'grandma_blue_5', 'grandma_blue_6',
      'grandma_letter_1', 'grandma_letter_2', 'grandma_letter_3', 'grandma_letter_4',
      'grandma_letter_5', 'grandma_letter_6', 'grandma_letter_7',
    ];
    voices.forEach(slug => {
      scene.load.audio(slug, `${base}/${slug}.mp3`);
    });

    scene.load.on('loaderror', (file) => {
      console.warn(`[Audio] missing: ${file.key}`);
    });
  }

  /** Play a music track, crossfading from whatever was playing.
   *  Authoritatively stops any OTHER music tracks in the global sound
   *  manager — the per-scene _currentMusic ref goes stale on scene changes
   *  (Phaser sounds are game-scoped, not scene-scoped).
   */
  playMusic(key, { loop = true, fadeIn = 2000, fadeOut = 1200, volume = null } = {}) {
    if (!this.scene || !this._enabled) return;
    const scene = this.scene;
    const targetVol = volume ?? this._musicVolume;
    if (!scene.cache.audio.exists(key)) {
      console.warn(`[Audio] music not loaded: ${key}`);
      return;
    }

    const soundMgr = scene.sound;

    // Is this track already playing? Don't restart.
    const alreadyPlaying = soundMgr.sounds.find(s => s.key === key && s.isPlaying);
    if (alreadyPlaying) {
      this._currentMusic = alreadyPlaying;
      // Make sure other music tracks are fading out
      this._fadeOutOtherMusic(key, fadeOut);
      return;
    }

    // Fade out ALL other music tracks currently playing
    this._fadeOutOtherMusic(key, fadeOut);

    // Start the new track at volume 0 and fade up
    const music = soundMgr.add(key, { loop, volume: 0 });
    music.play();
    scene.tweens.addCounter({
      from: 0, to: targetVol, duration: fadeIn,
      onUpdate: (tw) => { try { music.setVolume(tw.getValue()); } catch (_) {} },
    });
    this._currentMusic = music;
  }

  /** Fade out + remove every music track except `keepKey`. */
  _fadeOutOtherMusic(keepKey, fadeMs = 800) {
    if (!this.scene) return;
    const scene = this.scene;
    const sounds = scene.sound.sounds.slice(); // snapshot — mutations during iter
    for (const s of sounds) {
      if (!s || !s.key || s.key === keepKey) continue;
      if (!MUSIC_KEYS.has(s.key)) continue; // leave voice/SFX alone
      if (!s.isPlaying) { try { s.destroy(); } catch (_) {} continue; }
      const startVol = s.volume;
      scene.tweens.addCounter({
        from: startVol, to: 0, duration: fadeMs,
        onUpdate: (tw) => { try { s.setVolume(tw.getValue()); } catch (_) {} },
        onComplete: () => { try { s.stop(); s.destroy(); } catch (_) {} },
      });
    }
  }

  stopMusic(fadeOut = 1000) {
    if (!this._currentMusic || !this.scene) return;
    const old = this._currentMusic;
    this._currentMusic = null;
    const startVol = old.volume;
    this.scene.tweens.addCounter({
      from: startVol, to: 0, duration: fadeOut,
      onUpdate: (tw) => { try { old.setVolume(tw.getValue()); } catch (_) {} },
      onComplete: () => { try { old.stop(); old.destroy(); } catch (_) {} },
    });
  }

  /** Play a one-shot SFX */
  playSFX(key, { volume = null, rate = 1 } = {}) {
    if (!this.scene || !this._enabled) return;
    if (!this.scene.cache.audio.exists(key)) return;
    this.scene.sound.play(key, { volume: volume ?? this._sfxVolume, rate });
  }

  /** Play a voice line (returns the sound object so caller can wait for complete) */
  playVoice(key, { volume = null } = {}) {
    if (!this.scene || !this._enabled) return null;
    if (!this.scene.cache.audio.exists(key)) return null;
    const v = this.scene.sound.add(key, { volume: volume ?? this._voiceVolume });
    v.play();
    return v;
  }

  setEnabled(flag) {
    this._enabled = flag;
    if (!flag) this.stopMusic(400);
  }
}

export const Audio = new AudioManagerClass();
